import type { ProductOffering } from "./types";

export const PRODUCT_OFFERINGS: ProductOffering[] = [
  {
    kind: "product",
    id: "website",
    title: "Website",
    tagline: "Marketing sites and landing pages that convert and last.",
    description:
      "A focused marketing or company website — clear messaging, fast pages, and layouts that work on every device. Ideal when you need credibility and leads, not a full product platform.",
    whoFor:
      "Owners and teams who need a serious online presence: explain what you do, build trust, and give people a clear next step — without buying an app or store.",
    promise:
      "We ship on proven tools, measure what matters before launch, and leave you with a site that stays fast and maintainable.",
    outcomes: [
      "A live site visitors understand in seconds",
      "Clear path to contact or enquire",
      "Pages that stay quick on mobile",
      "Analytics and search basics ready to grow",
    ],
    includes: [
      "Audience, goals, and page structure",
      "Responsive design that matches your brand",
      "Build on WordPress or a modern static stack (your fit)",
      "Launch checks: speed, accessibility, SEO foundations",
      "Google Analytics & Search Console wired up",
    ],
    notIncluded: [
      "Online store / checkout (see E-shop)",
      "Ongoing ads management",
      "Native mobile apps",
    ],
    builtToLast: [
      "Known hosting and tools — not experimental frameworks by default",
      "Caching or CDN so the site feels snappy after go-live",
      "No page-builder lock-in unless we agree for a clear reason",
      "A short “how we run it” handoff so updates stay safe",
    ],
    price: "From €550",
    priceFromLabel: "From €550",
    timeline: "3–6 weeks",
    illustration: "/images/projects/web1.webp",
    createCta: "Create website",
    goal: "Capture leads and convert",
    processSteps: [
      {
        title: "Brief & goals",
        description:
          "Who you serve, what success looks like, and which pages actually matter — so scope stays honest.",
      },
      {
        title: "Structure & message",
        description:
          "Sitemap and key page outlines you can react to before visual design starts.",
      },
      {
        title: "Design",
        description:
          "High-fidelity layouts for mobile and desktop that feel like your brand, not a template.",
      },
      {
        title: "Build on a solid stack",
        description:
          "WordPress with a custom theme, or Netlify (Astro / Vite) when a lighter stack fits — proven plugins and packages only.",
      },
      {
        title: "Launch & handoff",
        description:
          "Performance and accessibility checks, analytics, domain notes, and a calm go-live.",
      },
    ],
    portfolioTypes: ["web-design"],
    exampleProjectIds: ["slovakfinance", "wildpoppy"],
    proofClaim:
      "Same craft as SlovakFinance — listing sites rebuilt for speed, trust, and a clear path to enquire.",
    featured: true,
  },
  {
    kind: "product",
    id: "eshop",
    title: "E-shop",
    tagline: "Online stores that sell — clear checkout, trust, and mobile shopping.",
    description:
      "A store people can browse and buy from with confidence — product discovery, cart, and checkout that feel trustworthy on mobile and desktop, usually on WooCommerce.",
    whoFor:
      "Makers, shops, and brands ready to sell online with a clear catalog — not a marketplace experiment.",
    promise:
      "Checkout stays reliable, the catalog stays fast, and we use tools shops already trust — not shiny plugins that break on Friday.",
    outcomes: [
      "A shop path from browse → product → pay that makes sense",
      "Trust signals and mobile-friendly buying",
      "A store you can restock without fighting the CMS",
      "Launch hygiene so speed and checkout both work",
    ],
    includes: [
      "Store structure: categories, product pages, cart & checkout",
      "WooCommerce setup with known payment and shipping options",
      "Mobile commerce patterns that reduce drop-off",
      "Caching that never breaks cart or account pages",
      "Analytics path ready for sales insights",
    ],
    notIncluded: [
      "Multi-vendor marketplaces",
      "Custom payment gateway development (unless quoted)",
      "ERP / warehouse integrations (unless quoted)",
    ],
    builtToLast: [
      "Boring, proven commerce stack — WooCommerce and maintained extensions",
      "Cart and checkout excluded from page cache so buyers aren’t blocked",
      "Purge-and-republish habits so new products show up reliably",
      "Security and update cadence agreed at handoff",
    ],
    price: "From €1,200",
    priceFromLabel: "From €1,200",
    timeline: "4–10 weeks",
    illustration: "/images/projects/2026/wildpoppy.sk/cover.png",
    createCta: "Create e-shop",
    goal: "Sell your products",
    processSteps: [
      {
        title: "Catalog & goals",
        description:
          "What you sell, who buys, and which platform fits — usually WooCommerce on solid hosting.",
      },
      {
        title: "Shop structure",
        description:
          "Categories, filters, and navigation that can grow with inventory.",
      },
      {
        title: "Product & checkout UX",
        description:
          "Listing, detail, cart, and payment flows — checked on a phone first.",
      },
      {
        title: "Build & brand",
        description:
          "Store look and feel plus a stable setup: payments, shipping, and safe caching.",
      },
      {
        title: "Launch & learn",
        description:
          "Go-live support, analytics, and a first pass on where people drop off.",
      },
    ],
    portfolioTypes: ["web-design"],
    exampleProjectIds: ["wildpoppy", "bbq"],
    proofClaim:
      "Grounded in shops like Wild Poppy Ceramics — WooCommerce rebuilt for speed, clear checkout, and fewer fragile plugins.",
    featured: true,
  },
  {
    kind: "product",
    id: "portfolio",
    title: "Portfolio",
    tagline: "A personal or studio site that shows your work properly.",
    description:
      "Case studies, project galleries, and contact paths that turn visitors into clients. Built for creatives, studios, and specialists who need their work to speak first.",
    whoFor:
      "Designers, photographers, studios, and specialists who win work when the portfolio is clear — not when the homepage is clever.",
    promise:
      "Your best work is easy to find, fast to load, and one step from “let’s talk.”",
    outcomes: [
      "Projects presented as proof, not a dump of images",
      "A hire / contact path that feels personal",
      "A light site that stays quick as the gallery grows",
      "Room to add cases without a redesign",
    ],
    includes: [
      "Case study and gallery templates",
      "Filtering or grouping that matches how you talk about work",
      "Mobile-first layouts",
      "Fast static hosting (typically Netlify)",
      "Contact or enquiry path + basic analytics",
    ],
    notIncluded: [
      "Full editorial blog / CMS (unless quoted)",
      "Long client CMS training programmes",
    ],
    builtToLast: [
      "Static or lightly dynamic stack — CDN by default",
      "Images and layout tuned so the portfolio itself isn’t slow",
      "Security headers and a clean deploy path",
      "Optional installable PWA when the site should feel app-like",
    ],
    price: "From €550",
    priceFromLabel: "From €550",
    timeline: "2–5 weeks",
    illustration: "/images/projects/2026/industry-photo.com/cover.png",
    createCta: "Create portfolio",
    goal: "Present your brand",
    processSteps: [
      {
        title: "Work & story",
        description:
          "Which projects prove you, and what story each case should tell.",
      },
      {
        title: "Structure",
        description:
          "Home, work index, case template, and contact — nothing extra unless it earns its place.",
      },
      {
        title: "Design",
        description:
          "Typography and layout that let the work dominate — calm chrome, strong images.",
      },
      {
        title: "Build & deploy",
        description:
          "Modern static stack on Netlify (or keep an existing Gatsby site healthy if that’s already home).",
      },
      {
        title: "Launch",
        description:
          "Domain, analytics, and a final pass so cases look sharp on mobile.",
      },
    ],
    portfolioTypes: ["web-design", "product-design"],
    exampleProjectIds: ["industryphoto", "polyphonicabbys"],
    proofClaim:
      "Like Industry Photo — a portfolio site where the work wins the quote, not the chrome.",
    featured: true,
  },
  {
    kind: "product",
    id: "web-app",
    title: "Web App",
    tagline: "SaaS, dashboards, and internal tools — designed and built to ship.",
    description:
      "Production-ready web applications: onboarding, dashboards, settings, and workflows. Design and modern front-end moving together — not mockups that die in a folder.",
    whoFor:
      "Founders and teams with a real product problem — an MVP slice you can put in users’ hands, not a wishlist of every feature.",
    promise:
      "We cut an MVP, ship in iterations you can demo weekly, and keep secrets and APIs behind a proper boundary.",
    outcomes: [
      "A scoped first version users can actually use",
      "Clear flows for the jobs that matter most",
      "A UI you can extend without starting over",
      "A deploy path your team can run again",
    ],
    includes: [
      "Discovery and MVP cut",
      "Product UX and information architecture",
      "UI design (and a small system if the product needs it)",
      "React / Vite build — TanStack Start when SSR helps",
      "Serverless functions for APIs and forms when needed",
    ],
    notIncluded: [
      "Unbounded “build everything” without an MVP cut",
      "Experimental frameworks without a clear reason",
    ],
    builtToLast: [
      "Mature React + Vite tooling — lockfiles and audits before launch",
      "Private keys stay on the server (Functions), never in the browser bundle",
      "Documented APIs and env names so handoff isn’t tribal knowledge",
      "Accessibility and performance treated as launch criteria, not polish week",
    ],
    price: "From €1,000",
    priceFromLabel: "From €1,000",
    timeline: "8–12+ weeks",
    illustration: "/images/projects/2018/alloy/1.webp",
    createCta: "Create web app",
    goal: "Reach new markets",
    processSteps: [
      {
        title: "Discovery & MVP",
        description:
          "Users, jobs-to-be-done, and the thinnest slice worth shipping first.",
      },
      {
        title: "Flows & structure",
        description:
          "End-to-end journeys for core features before visual polish.",
      },
      {
        title: "UI & components",
        description:
          "Screens and patterns your product can reuse as it grows.",
      },
      {
        title: "Build in iterations",
        description:
          "Weekly demos — design and code moving together on Netlify.",
      },
      {
        title: "Harden & launch",
        description:
          "Empty states, edge cases, performance, and a release you can repeat.",
      },
    ],
    portfolioTypes: ["product-design"],
    exampleProjectIds: ["alloy", "intobridge"],
    proofClaim:
      "Built like Alloy and IntoBridge — product UX that stays clear when the feature set grows.",
    featured: true,
  },
  {
    kind: "product",
    id: "mobile-app",
    title: "Mobile App",
    tagline: "iOS and Android experiences that feel native to each platform.",
    description:
      "App UX and UI for iPhone, iPad, and Android — flows, components, and visuals aligned to platform guidelines, with prototypes and specs ready for engineering or store submission support.",
    whoFor:
      "Teams that need a native-feeling app experience designed properly — before (or while) engineers build.",
    promise:
      "You get flows and UI that respect iOS and Android — plus a prototype and handoff your developers can trust.",
    outcomes: [
      "A clear v1 feature set per platform",
      "Tap-through prototype for stakeholders or tests",
      "High-fidelity UI ready for build",
      "Specs and assets that reduce “what did design mean?”",
    ],
    includes: [
      "App UX and screen flows",
      "High-fidelity UI per platform",
      "Clickable prototype",
      "Handoff specs and exportable assets",
      "Store listing asset support when needed",
    ],
    notIncluded: [
      "Full native engineering (unless separately quoted)",
      "App Store / Play release management (unless quoted)",
      "Backend API build (pair with Web App or your team)",
    ],
    builtToLast: [
      "Platform patterns first — fewer custom controls that age badly",
      "Prototypes that expose real gaps before code spend",
      "Handoff completeness checked before “design done”",
      "Honest scope: design-led unless we quote build partners",
    ],
    price: "From €500",
    priceFromLabel: "From €500",
    timeline: "6–12 weeks",
    illustration: "/images/projects/mobile1.webp",
    createCta: "Create mobile app",
    goal: "Create something special",
    processSteps: [
      {
        title: "Platform & scope",
        description:
          "iOS, Android, or both — and which features belong in v1.",
      },
      {
        title: "User flows",
        description:
          "Navigation and key tasks mapped screen by screen.",
      },
      {
        title: "UI design",
        description:
          "Native patterns, type, and components for each OS.",
      },
      {
        title: "Prototype & feedback",
        description:
          "Tap-through reviews with stakeholders or early users.",
      },
      {
        title: "Handoff",
        description:
          "Specs, redlines, and assets for engineering — or store creative if that’s the next step.",
      },
    ],
    portfolioTypes: ["mobile-design"],
    exampleProjectIds: ["alloy-mobile", "toonect-mobile"],
    proofClaim:
      "From Alloy and TooNect mobile work — flows designed for thumbs, not just desktop ports.",
  },
  {
    kind: "product",
    id: "brand-identity",
    title: "Brand Identity",
    tagline: "Logo, visual language, and guidelines for a coherent brand.",
    description:
      "Identity for products and companies launching or refreshing — logo, colour, typography, and examples of how the brand lives in digital touchpoints.",
    whoFor:
      "Founders and teams who need a coherent look before (or while) the website and product catch up.",
    promise:
      "A system you can hand to designers and developers — not a logo file lost in email.",
    outcomes: [
      "A mark and wordmark you can defend",
      "Colour and type that work in digital UI",
      "Guidelines your team can follow without guessing",
      "Mockups that show the brand on real surfaces",
    ],
    includes: [
      "Logo and logotype exploration",
      "Colour and typography system",
      "Digital style guide",
      "Application mockups (web / app / marketing)",
      "File handoff for ongoing use",
    ],
    notIncluded: [
      "Full website or app build (see Website / Web App)",
      "Print production management (unless quoted)",
    ],
    builtToLast: [
      "Rules written for digital use — contrast, sizes, do/don’t",
      "Assets organised for the next designer or developer",
      "Applications that prove the system before you scale spend",
      "Easy upsell path into a site or micro page when you’re ready",
    ],
    price: "From €300",
    priceFromLabel: "From €300",
    timeline: "2–4 weeks",
    illustration: "/images/projects/2013/toonect/Toonect-Instantly-Reachable.png",
    createCta: "Create brand identity",
    goal: "Create remarkable brand",
    processSteps: [
      {
        title: "Brand discovery",
        description:
          "Positioning, audience, and references — what the brand should feel like.",
      },
      {
        title: "Concept directions",
        description:
          "Two or three visual routes before we commit to one system.",
      },
      {
        title: "Identity system",
        description:
          "Logo, palette, type, and usage rules.",
      },
      {
        title: "Applications",
        description:
          "Examples on web, app, and marketing materials.",
      },
      {
        title: "Guidelines & handoff",
        description:
          "Files and documentation for your team and partners.",
      },
    ],
    portfolioTypes: ["identity"],
    exampleProjectIds: ["toonect-identity", "alloy-identity"],
    proofClaim:
      "Like Toonect Identity — a brand system built to live in product, marketing, and mobile.",
  },
  {
    kind: "product",
    id: "games-interactive",
    title: "Games & Interactive",
    tagline: "UI, branding, and front-end for games and playful products.",
    description:
      "Complex interfaces, in-game UI, and interactive experiences — where clarity and feel matter as much as polish.",
    whoFor:
      "Game and interactive product teams who need UI and front-end craft that survives real play, not just art slides.",
    promise:
      "We design for the loop players actually feel — then support build and iteration from real sessions.",
    outcomes: [
      "UI that communicates state under pressure",
      "Motion and feedback that help, not distract",
      "In-product branding that fits the world",
      "A collaboration rhythm with your engineers",
    ],
    includes: [
      "Game and product UI design",
      "Interaction and motion direction",
      "In-product branding",
      "Front-end collaboration or a quoted build slice",
      "Iteration from playtests",
    ],
    notIncluded: [
      "Full engine / multiplayer backend (unless quoted)",
      "Open-ended live-ops without a retainer",
    ],
    builtToLast: [
      "Stack agreed per title — no mystery frameworks mid-project",
      "Playtest feedback closed in the process, not “later”",
      "Front-end patterns your team can keep shipping",
      "Scope written so “interactive” doesn’t mean infinite",
    ],
    price: "On request",
    priceFromLabel: "On request",
    timeline: "Project-based",
    illustration: "/images/projects/2024/intobridge/intobridge-cover.png",
    createCta: "Request a quote",
    goal: "Create good time",
    processSteps: [
      {
        title: "Experience goals",
        description:
          "Player mindset, core loop, and what the UI must communicate.",
      },
      {
        title: "UX architecture",
        description:
          "Screens, states, and flows for complex interactive systems.",
      },
      {
        title: "Visual & motion",
        description:
          "Look and feedback that support play without hurting usability.",
      },
      {
        title: "Build collaboration",
        description:
          "Work with your engineers on components, CSS, and live tuning — or a quoted front-end slice.",
      },
      {
        title: "Playtest & iterate",
        description:
          "Refine from real sessions and whatever signals you already collect.",
      },
    ],
    portfolioTypes: ["product-design", "motion-design"],
    exampleProjectIds: ["intobridge"],
    proofClaim:
      "Playful product craft from IntoBridge — interaction that teaches without friction.",
  },
  {
    kind: "product",
    id: "micro-page",
    title: "Micro Page",
    tagline: "One focused page — live fast, without a full site project.",
    description:
      "A single landing or brochure page when you need to be online quickly: launch, campaign, waitlist, or a simple “who we are + contact” presence. Fixed scope — one page that works.",
    whoFor:
      "Anyone who needs a sharp online foothold now — event, offer, or studio — without commissioning a full website.",
    promise:
      "One page, clear action, live in about one to two weeks on a light, proven stack.",
    outcomes: [
      "A live URL you can share immediately",
      "One clear action (contact, book, join)",
      "Mobile-first layout that loads quickly",
      "Room to grow into a full site later",
    ],
    includes: [
      "One-page structure and message guidance",
      "Responsive visual design",
      "Contact or primary CTA",
      "Lightweight build, basic SEO, and launch",
      "Domain pointing notes (often to Netlify)",
    ],
    notIncluded: [
      "Multi-page sitemap or blog",
      "CMS or e-shop",
      "Ongoing content production",
    ],
    builtToLast: [
      "Minimal stack — Astro or Vite on Netlify by default",
      "Fast by design: little chrome, little JavaScript",
      "Analytics if you’ll run traffic",
      "Clean upgrade path to a full Website product later",
    ],
    price: "From €260",
    priceFromLabel: "From €260",
    timeline: "1–2 weeks",
    illustration: "/images/projects/2014/guvernante/_Guvernante.com/guvernante-webdesign.png",
    createCta: "Create micro page",
    goal: "Sell products in focus",
    processSteps: [
      {
        title: "Brief & message",
        description:
          "One call or short form — purpose, audience, and the action you want.",
      },
      {
        title: "Single-page design",
        description:
          "Hero, proof, offer, and contact — clear on mobile and desktop.",
      },
      {
        title: "Build & launch",
        description:
          "Lightweight implementation, basic SEO, domain notes, and handover.",
      },
    ],
    portfolioTypes: ["web-design"],
    exampleProjectIds: ["guvernante", "wildpoppy"],
    proofClaim:
      "Same focus as Guvernante — one sharp page where imagery and action carry the brand.",
  },
];

export function getProductById(id: string): ProductOffering | undefined {
  return PRODUCT_OFFERINGS.find((p) => p.id === id);
}
